// src/composer/hooks/useShareURL.ts
import { useEffect, useRef } from 'react';
import { encodeShare, decodeShare } from '../share';
import type { SharePayload } from '../share';
import type { ContentPack, BuildState } from '../../packs/types';
import type { NavigationAdapter } from '../../adapters/types';
import type { ColorState } from '../colors';

interface UseShareURLState {
  build: BuildState;
  colors: ColorState;
}

export function useShareURL(
  pack: ContentPack,
  navigation: NavigationAdapter,
  state: UseShareURLState,
  onLoad: (p: SharePayload) => void,
) {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onLoadRef = useRef(onLoad);
  useEffect(() => { onLoadRef.current = onLoad; });

  // On mount: read hash and apply
  useEffect(() => {
    const hash = navigation.readHash();
    const match = hash.match(/[#&]s=([^&]*)/);
    if (!match) return;
    const payload = decodeShare(match[1]);
    if (payload) onLoadRef.current(payload);
  }, [navigation]);

  // On state change: write hash debounced 250ms
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const encoded = encodeShare(pack.id, pack.version, state);
      navigation.writeHash(`#s=${encoded}`);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [pack.id, pack.version, state, navigation]);
}
